import React, { useMemo } from "react";
import { AspectRatio, Box, Flex, Image, SimpleGrid, Text } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { getBlogPostsForLanguage, getMarketingPageHeading, MarketingContentIndexEntry } from "../../marketing-content/marketingContentLoader";
import { useLandingTheme } from "../landing-page/LandingThemeContext";
import { ThemedCaption, ThemedContainer, ThemedEyebrow } from "../landing-page/sections/themed/ThemedLayout";
import { MarketingPageMain } from "./MarketingSiteShell";
import { formatMarketingPublishedDate } from "./marketingSiteCopy";
import { MarketingTemplatePageProps } from "./marketingTemplatePageProps";

const BlogPostCard = React.memo(function BlogPostCard({ post }: { post: MarketingContentIndexEntry }) {
  const theme = useLandingTheme();
  return (
    <Box
      as={RouterLink}
      to={post.route}
      display="flex"
      flexDirection="column"
      bg={theme.color.surface}
      border={theme.card.border}
      boxShadow={theme.card.shadow}
      borderRadius={theme.shape.cardRadius}
      overflow="hidden"
      transition="transform 0.2s ease"
      _hover={{ transform: "translateY(-3px)", textDecoration: "none" }}
      _focusVisible={{ boxShadow: `0 0 0 3px ${theme.color.highlight}` }}
    >
      <AspectRatio ratio={16 / 9} w="100%" bg={theme.color.surfaceAlt}>
        {post.heroImage ? <Image src={post.heroImage} alt="" objectFit="cover" loading="lazy" /> : <Box />}
      </AspectRatio>
      <Flex direction="column" gap="8px" p={{ base: "18px", md: "22px" }}>
        {post.category && <ThemedEyebrow fontSize="12px">{post.category}</ThemedEyebrow>}
        <Text as="h2" fontFamily={theme.font.display} fontWeight={700} fontSize={{ base: "19px", md: "20px" }} lineHeight={1.25} color={theme.color.heading}>
          {getMarketingPageHeading(post)}
        </Text>
        {post.publishedDate && <ThemedCaption>{formatMarketingPublishedDate(post.publishedDate, post.lang)}</ThemedCaption>}
      </Flex>
    </Box>
  );
});

/**
 * The `blogIndex` template: the heading from the front matter and a card grid of every post in the
 * page's language, newest first. The cards are plain page content rather than a virtualized list:
 * the page has to scroll natively and stay crawlable, and the images are lazy-loaded.
 */
export const BlogIndexPage = React.memo(function BlogIndexPage({ entry }: MarketingTemplatePageProps) {
  const posts = useMemo(() => getBlogPostsForLanguage(entry.lang), [entry.lang]);
  return (
    <MarketingPageMain template="blogIndex">
      <article className="marketing-article">
        <h1 className="marketing-article-heading">{getMarketingPageHeading(entry)}</h1>
      </article>
      <ThemedContainer pb={{ base: "64px", lg: "96px" }}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: "24px", lg: "32px" }}>
          {posts.map((post) => (
            <BlogPostCard key={post.route} post={post} />
          ))}
        </SimpleGrid>
      </ThemedContainer>
    </MarketingPageMain>
  );
});
